import { faker } from "@faker-js/faker";
import { GenderApiValues } from "@utils/constants";
import { getGenderValue, setGenderValue } from "@utils/helpers";
import { format } from "date-fns";
import { PropsWithChildren, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button, Headline, SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  AddReportClientDetailsMutationVariables,
  ApiReportTemplateFileUploadConfigChoices,
  useAddReportClientDetailsMutation,
  useUpdateReportClientDetailsMutation,
} from "@api/graphql/types-and-hooks";
import { DevOnlyWrapper } from "@components/DevOnlyWrapper/DevOnlyWrapper";
import { DoubleColumnContainer, FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { FooterButtonRow } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/FooterButtonRow";
import { getOrderedAssessmentReportSubRoutes } from "@pages/AssessmentReportV2Details/helpers";
import { InitialAssessmentPageRoute } from "@pages/AssessmentReportV2Details/types";
import { useReport } from "@providers/ReportProvider";

interface RowProps extends PropsWithChildren {
  title: string;
  isLast?: boolean;
}
const Row: React.FC<RowProps> = ({ title, children }) => {
  return (
    <div className="py-6 flex w-full flex-col items-end">
      <div className="grid w-full grid-cols-8 items-start">
        <div className="col-span-2 flex flex-col">
          <Headline size="medium">{title}</Headline>
        </div>
        <div className="col-span-6 flex flex-col bg-bg-surface outline-solid outline-1 outline-border-medium px-8 py-6 rounded-3xl">
          {children}
        </div>
      </div>
    </div>
  );
};

export const ClientDetails: React.FC = () => {
  const { report, reportQuery } = useReport();
  const isCatalightTemplate =
    report?.reportTemplate?.fileUploadConfig === ApiReportTemplateFileUploadConfigChoices.Catalight.valueOf();
  const learnerId = report?.learner?.id || null;
  const go = useNavigate();
  const { pathname } = useLocation();

  const addAssessmentClientDetailsMutation = useAddReportClientDetailsMutation({});
  const updateAssessmentClientDetailsMutation = useUpdateReportClientDetailsMutation({});

  const currentRoute =
    (pathname.split("#")?.pop()?.split("/").pop() as InitialAssessmentPageRoute) ||
    ("upload-files" as InitialAssessmentPageRoute);
  const { getNextRoute } = getOrderedAssessmentReportSubRoutes();
  const nextRoute = getNextRoute(currentRoute);

  const { isPending: saveCallLoading } = addAssessmentClientDetailsMutation;
  const { isPending: updateCallLoading } = updateAssessmentClientDetailsMutation;

  const { onSubmit, formState, RegisteredFormInput, RegisteredFormSelected, reset, watch, setValue, trigger } =
    useFormUtils<AddReportClientDetailsMutationVariables>({
      mutationFn: async (params) => {
        if (!learnerId) {
          await addAssessmentClientDetailsMutation.mutateAsync(
            {
              reportId: report?.id ?? "<missing-assessment-id>",
              learnerCaregiverMinimalInput: {
                ...params.learnerCaregiverMinimalInput,
                gender: setGenderValue(params.learnerCaregiverMinimalInput.gender),
              },
            },
            {
              onSuccess: async () => {
                await reportQuery.refetch(); // refetch assessmentBuilderProvide data
                notifySuccess("Successfully Created Client");
                go(`../${nextRoute}`);
              },
              onError: async (error) => {
                console.error("Error creating Client> ", error);
                notifyError("Error Creating Client");
              },
            },
          );
        } else {
          await updateAssessmentClientDetailsMutation.mutateAsync(
            {
              reportId: report?.id ?? "<missing-assessment-id>",
              learnerCaregiverMinimalInput: {
                ...params.learnerCaregiverMinimalInput,
                gender: setGenderValue(params.learnerCaregiverMinimalInput.gender),
              },
              updateLearner: false,
            },
            {
              onSuccess: async () => {
                await reportQuery.refetch();
                notifySuccess("Successfully Updated Client Details");
                go(`../${nextRoute}`);
              },
              onError: async (error) => {
                console.error("Error updating client details", error);
                notifyError("Error Updating Client Details");
              },
            },
          );
        }
      },
    });

  useEffect(() => {
    if (report) {
      const formattedGender = getGenderValue(report?.reportClientDetails?.gender) || "";
      reset({
        learnerCaregiverMinimalInput: {
          clientFirstName: report?.reportClientDetails?.clientFirstName,
          clientLastName: report?.reportClientDetails?.clientLastName || "",
          birthDate: report?.reportClientDetails?.birthDate,
          address: report?.reportClientDetails?.address || "",
          addressLine2: report?.reportClientDetails?.addressLine2,
          gender: formattedGender,
          specifyGender: report?.reportClientDetails?.specifyGender || "",
          city: report?.reportClientDetails?.city,
          state: report?.reportClientDetails?.state,
          zip: report?.reportClientDetails?.zip,
          caregiverFirstName: report?.reportClientDetails?.guardianFirstName,
          caregiverLastName: report?.reportClientDetails?.guardianLastName || "",
          caregiver2FirstName: report?.reportClientDetails?.guardian2FirstName,
          caregiver2LastName: report?.reportClientDetails?.guardian2LastName || "",
        },
      });
    } else {
      reset({ learnerCaregiverMinimalInput: {} });
    }
  }, [report]);

  const genderWatch = watch("learnerCaregiverMinimalInput.gender");

  const isReadOnly = learnerId && !isCatalightTemplate ? true : false;
  const isPending = saveCallLoading || updateCallLoading;

  return (
    <div className="flex w-full flex-col px-14 py-4">
      <FormContainer onSubmit={onSubmit}>
        <Row title="Client Details">
          {!isReadOnly ? (
            <DevOnlyWrapper>
              <div className="my-4 flex items-center">
                <Button
                  appearance="link"
                  className="mr-2"
                  text="Fill Fields (dev only)"
                  onClick={() => {
                    setValue("learnerCaregiverMinimalInput.clientFirstName", `(fake) ${faker.person.firstName()}`);
                    setValue("learnerCaregiverMinimalInput.clientLastName", `(fake) ${faker.person.lastName()}`);
                    setValue(`learnerCaregiverMinimalInput.birthDate`, format(faker.date.birthdate(), "yyyy-MM-dd"), {
                      shouldValidate: true,
                    });
                    setValue(
                      "learnerCaregiverMinimalInput.gender",
                      `${faker.helpers.arrayElement(["Boy/man", "Girl/woman"])}`,
                    );
                    setValue("learnerCaregiverMinimalInput.address", `${faker.location.streetAddress()}`);
                    setValue("learnerCaregiverMinimalInput.addressLine2", `${faker.location.secondaryAddress()}`);
                    setValue("learnerCaregiverMinimalInput.city", `${faker.location.city()}`);
                    setValue("learnerCaregiverMinimalInput.state", `${faker.location.state()}`);
                    setValue("learnerCaregiverMinimalInput.zip", `${faker.location.zipCode()}`);
                    setValue("learnerCaregiverMinimalInput.caregiverFirstName", `(fake) ${faker.person.firstName()}`);
                    setValue("learnerCaregiverMinimalInput.caregiverLastName", `(fake) ${faker.person.lastName()}`);
                    setValue("learnerCaregiverMinimalInput.caregiver2FirstName", `(fake) ${faker.person.firstName()}`);
                    setValue("learnerCaregiverMinimalInput.caregiver2LastName", `(fake) ${faker.person.lastName()}`);
                    trigger();
                  }}
                />
                <Button
                  appearance="link"
                  text="Clear Fields (dev only)"
                  onClick={() => {
                    reset();
                    trigger();
                  }}
                />
              </div>
            </DevOnlyWrapper>
          ) : null}
          <div className="col-span-3 flex flex-col">
            <div className="gap-x-4  lg:grid lg:grid-cols-2">
              <RegisteredFormInput
                formKey="learnerCaregiverMinimalInput.clientFirstName"
                inputSize="full"
                formState={formState}
                label="Client’s First Name"
                readOnly={isReadOnly}
              />
              <RegisteredFormInput
                formKey="learnerCaregiverMinimalInput.clientLastName"
                inputSize="full"
                formState={formState}
                label="Client's Last Name"
                readOnly={isReadOnly}
              />
            </div>
          </div>
          <div className="gap-x-4 lg:grid lg:grid-cols-2">
            <RegisteredFormInput
              formKey="learnerCaregiverMinimalInput.birthDate"
              inputSize="full"
              type="date"
              formState={formState}
              label="Date of Birth"
              readOnly={isReadOnly}
            />
            <div />
          </div>
          {isReadOnly ? (
            <DoubleColumnContainer>
              <RegisteredFormInput
                formKey="learnerCaregiverMinimalInput.gender"
                formState={formState}
                label="Gender"
                readOnly
                inputSize="full"
              />
              {genderWatch === "Gender identity is not listed" && (
                <RegisteredFormInput
                  formKey="learnerCaregiverMinimalInput.specifyGender"
                  formState={formState}
                  required={false}
                  label="Specify Gender"
                  readOnly
                  inputSize="full"
                />
              )}
            </DoubleColumnContainer>
          ) : (
            <DoubleColumnContainer>
              <RegisteredFormSelected
                formKey="learnerCaregiverMinimalInput.gender"
                formState={formState}
                placeholderText={""}
                title="Gender"
                readOnly={isReadOnly}
                items={
                  Object.values(GenderApiValues).map((o) => ({
                    primary: getGenderValue(o),
                  })) as SelectItem[]
                }
              />
              {genderWatch === "Gender identity is not listed" && (
                <RegisteredFormInput
                  formKey="learnerCaregiverMinimalInput.specifyGender"
                  formState={formState}
                  required={false}
                  label="Specify Gender"
                  readOnly={isReadOnly}
                />
              )}
            </DoubleColumnContainer>
          )}

          <div className="col-span-3 flex flex-col">
            <div className="gap-x-4  lg:grid lg:grid-cols-2">
              <RegisteredFormInput
                formKey="learnerCaregiverMinimalInput.address"
                inputSize="full"
                required={false}
                formState={formState}
                label="Address Line 1"
                readOnly={isReadOnly}
              />
              <RegisteredFormInput
                formKey="learnerCaregiverMinimalInput.addressLine2"
                inputSize="full"
                required={false}
                formState={formState}
                label="Address Line 2"
                readOnly={isReadOnly}
              />
            </div>
          </div>
          <div className="col-span-3 flex flex-col">
            <div className="gap-x-4  lg:grid lg:grid-cols-2">
              <RegisteredFormInput
                formKey="learnerCaregiverMinimalInput.city"
                inputSize="full"
                required={false}
                formState={formState}
                label="City"
                readOnly={isReadOnly}
              />

              <div className="gap-x-4  lg:grid lg:grid-cols-2">
                <RegisteredFormInput
                  formKey="learnerCaregiverMinimalInput.state"
                  inputSize="full"
                  required={false}
                  formState={formState}
                  label="State / Province"
                  readOnly={isReadOnly}
                />
                <RegisteredFormInput
                  formKey="learnerCaregiverMinimalInput.zip"
                  inputSize="full"
                  required={false}
                  formState={formState}
                  label="ZIP / Postal Code"
                  readOnly={isReadOnly}
                />
              </div>
            </div>
          </div>

          <div className="col-span-3 flex flex-col">
            <div className="gap-x-4  lg:grid lg:grid-cols-2">
              <RegisteredFormInput
                formKey="learnerCaregiverMinimalInput.caregiverFirstName"
                inputSize="full"
                formState={formState}
                label="Guardian’s First Name"
                readOnly={isReadOnly}
              />
              <RegisteredFormInput
                formKey="learnerCaregiverMinimalInput.caregiverLastName"
                inputSize="full"
                formState={formState}
                label="Guardian’s Last Name"
                readOnly={isReadOnly}
              />
            </div>
            <div className="gap-x-4  lg:grid lg:grid-cols-2">
              <RegisteredFormInput
                formKey="learnerCaregiverMinimalInput.caregiver2FirstName"
                inputSize="full"
                formState={formState}
                label="Second Guardian’s First Name"
                required={false}
                readOnly={isReadOnly}
              />
              <RegisteredFormInput
                formKey="learnerCaregiverMinimalInput.caregiver2LastName"
                inputSize="full"
                formState={formState}
                label="Second Guardian’s Last Name"
                required={false}
                readOnly={isReadOnly}
              />
            </div>
          </div>
        </Row>
        {isReadOnly ? (
          <FooterButtonRow primaryButtonTitle="Next Step" hideBackButton={true} />
        ) : (
          <FooterButtonRow
            primaryButtonTitle="Next Step"
            primaryButtonType="submit"
            primaryButtonLoading={isPending}
            hideBackButton={true}
          />
        )}
      </FormContainer>
    </div>
  );
};
